import { CheckCircle, CornerLeftUp, FileText, Lock, MoreVertical, Video } from 'react-feather';
import Tooltip from '../../popups/Tooltip';
import { groupModeOf } from '../../../Utils/customGroupsHelpers';

// A single group row in either the Pending or the Ready list. Pure
// presentation: every interaction surfaces as a callback. The Pending
// variant carries inline rename, a group-level mode toggle (when Linked),
// a Move-to-Ready quick action, and the kebab menu trigger. The Ready
// variant trims those down to just "Move back to Pending".
//
// Props:
//   group, members, status ('pending' | 'ready'), videoModes
//   isSelected, isExpanded
//   selectedFundNumber, recentlyMoved
//   renamingGroupId, renameValue (Pending only)
//   isKebabOpen (Pending only)
//   onHeaderClick(), onMemberClick(member), onMemberToggleMode(member, e)
//   onGroupToggleMode(members, e) (Pending+Linked only)
//   onMoveGroup() — Pending → Ready, Ready → Pending
//   onCommitRename(), onCancelRename(), onRenameChange(value) (Pending only)
//   onKebabClick(e) (Pending only; caller computes kebabPos)
function PortfolioGroup({
    group, members, status, videoModes,
    showVideoToggle = true,
    isSelected, isExpanded,
    selectedFundNumber, recentlyMoved,
    renamingGroupId, renameValue,
    isKebabOpen,
    onHeaderClick, onMemberClick, onMemberToggleMode,
    onGroupToggleMode, onMoveGroup,
    onCommitRename, onCancelRename, onRenameChange,
    onKebabClick,
}) {
    const isLinked = group.linked !== false;
    const isReadonly = isLinked;        // Linked groups: members are read-only
    const isIndividual = !isLinked;
    const isRenaming = status === 'pending' && renamingGroupId === group.id;
    const flashClass = status === 'pending' && recentlyMoved === group.id ? ' sg-group--flash' : '';

    return (
        <div className={`sg-group${isSelected ? ' sg-group--selected' : ''}${flashClass}`}>
            <div className="sg-header" onClick={onHeaderClick}>
                <div className="sg-name-group" onClick={e => isRenaming && e.stopPropagation()}>
                    {isRenaming ? (
                        <input
                            className="sg-rename-input"
                            value={renameValue}
                            autoFocus
                            onChange={e => onRenameChange(e.target.value)}
                            onBlur={onCommitRename}
                            onKeyDown={e => {
                                if (e.key === 'Enter') onCommitRename();
                                if (e.key === 'Escape') onCancelRename();
                            }}
                            onClick={e => e.stopPropagation()}
                        />
                    ) : (
                        <span className="sg-name">{group.name}</span>
                    )}
                    <span className={`sg-pill${isSelected ? ' sg-pill--selected' : ''}`}>{members.length}</span>
                    {isLinked && <Lock size={10} className="sg-linked-icon" />}
                </div>

                {/* Group-level mode toggle: Pending + Linked only */}
                {status === 'pending' && isLinked && showVideoToggle && (() => {
                    const gMode = groupModeOf(group, members, videoModes);
                    return (
                        <Tooltip label={gMode === 'video' ? 'Switch to Editor' : 'Switch to Video'} placement="top" delay={600}>
                            <button
                                className={`sg-mode-btn${gMode === 'video' ? ' sg-mode-btn--video' : ''}`}
                                onClick={e => onGroupToggleMode(members, e)}
                            >
                                {gMode === 'video'
                                    ? <Video size={12} style={{ color: '#1FA3FF' }} />
                                    : <FileText size={12} style={{ color: '#1FA3FF' }} />}
                            </button>
                        </Tooltip>
                    );
                })()}

                {/* Quick action: Move to Ready (Pending) or Move back to Pending (Ready) */}
                {status === 'pending' ? (
                    <Tooltip label="Move to Ready to Publish" placement="top" delay={400}>
                        <button className="sg-mode-btn" onClick={e => { e.stopPropagation(); onMoveGroup(); }}>
                            <CheckCircle size={12} style={{ color: '#C2C4CE' }} />
                        </button>
                    </Tooltip>
                ) : (
                    <div className="sg-kebab-wrap" onClick={e => e.stopPropagation()}>
                        <Tooltip label="Move back to Pending" placement="top" delay={400}>
                            <button
                                className="sg-kebab-btn"
                                onClick={e => { e.stopPropagation(); onMoveGroup(); }}
                                aria-label="Move back to Pending"
                            >
                                <CornerLeftUp size={14} />
                            </button>
                        </Tooltip>
                    </div>
                )}

                {/* Kebab menu trigger: Pending only */}
                {status === 'pending' && (
                    <div className="sg-kebab-wrap" onClick={e => e.stopPropagation()}>
                        <button
                            className={`sg-kebab-btn${isKebabOpen ? ' sg-kebab-btn--open' : ''}`}
                            onClick={onKebabClick}
                            aria-label="Group actions"
                            aria-expanded={isKebabOpen}
                        >
                            <MoreVertical size={16} />
                        </button>
                    </div>
                )}
            </div>

            {isExpanded && members.length > 0 && (
                <ul className="sg-body">
                    {members.map(p => {
                        const isMemberSelected = isSelected && selectedFundNumber === p.fundNumber;
                        const isVideo = videoModes[p.fundNumber] === 'video';
                        return (
                            <li
                                key={p.fundNumber}
                                className={`sg-row${(isMemberSelected && !isReadonly) ? ' sg-row--selected' : ''}${isReadonly ? ' sg-row--readonly' : ''}${isIndividual ? ' sg-row--individual' : ''}`}
                                onClick={isReadonly ? undefined : () => onMemberClick(p)}
                            >
                                {/* pointerEvents:auto re-enables hover on the name even when the row is
                                    Linked/readonly (.sg-row--readonly sets pointer-events:none), so the
                                    full-name tooltip fires like it does for ungrouped members. */}
                                <Tooltip label={p.fundName} placement="right" delay={1200} outerStyle={{ flex: 1, minWidth: 0, pointerEvents: 'auto' }}>
                                    <span className="sg-row-name">{p.fundName}</span>
                                </Tooltip>
                                {!isReadonly && showVideoToggle && (
                                    <Tooltip label={isVideo ? 'Switch to Editor' : 'Switch to Video'} placement="top" delay={600}>
                                        <button
                                            className={`sg-mode-btn${isVideo ? ' sg-mode-btn--video' : ''}`}
                                            onClick={e => onMemberToggleMode(p, e)}
                                        >
                                            {isVideo
                                                ? <Video size={12} style={{ color: '#1FA3FF' }} />
                                                : <FileText size={12} style={{ color: '#1FA3FF' }} />}
                                        </button>
                                    </Tooltip>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default PortfolioGroup;
